import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { axiosInstance } from "@/lib/api";
import { POLL_CURATION_MS, clampWhenHidden } from "@/lib/realtime";
import type {
  WorkshopContextItem,
  WorkshopContextItemKind,
} from "@/types/workshop.type";
import { workshopContextKey } from "./workshopKeys";

export function useWorkshopContextItems(projectId: string | null) {
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: workshopContextKey(projectId),
    queryFn: async () => {
      const res = await axiosInstance.get<{ items: WorkshopContextItem[] }>(
        `/workshop/projects/${projectId}/context`,
      );
      return res.data.items;
    },
    enabled: !!projectId,
    // The chat agent adds/removes items too (invalidated live in this tab, but
    // not from a teammate's tab) — keep a slow floor.
    refetchInterval: () => clampWhenHidden(POLL_CURATION_MS),
  });

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: workshopContextKey(projectId) });
  };

  const addItem = useMutation({
    mutationFn: async (vars: {
      kind: WorkshopContextItemKind;
      targetId?: string;
      url?: string;
      label: string;
      note?: string;
    }) => {
      const res = await axiosInstance.post<{ item: WorkshopContextItem }>(
        `/workshop/projects/${projectId}/context`,
        vars,
      );
      return res.data.item;
    },
    onSuccess: invalidate,
  });

  const removeItem = useMutation({
    mutationFn: async (contextItemId: string) => {
      await axiosInstance.delete(`/workshop/context/${contextItemId}`);
    },
    onSuccess: invalidate,
  });

  return {
    items: query.data ?? [],
    isLoading: query.isLoading,
    /** The read failed and nothing was ever read: `items` is not a real empty list. */
    isError: query.isError && query.data === undefined,
    refetch: query.refetch,
    addItem,
    removeItem,
  };
}
